import {useSelector} from 'react-redux';
import {useVersioned} from 'coreUtils/adapters/versioned';
import {selectProductSearchResultByKey, selectProductsHits} from 'coreStore/data/productSearchResult';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import AbstractSearchProvider from 'coreClasses/search/providers/AbstractSearchProvider';
import {ESearchProvider, ESearchType} from 'coreClasses/search/ISearch';
import {useConfiguration} from 'coreHooks';
import {DEFAULT_KEY} from 'coreClasses/search/SearchMgr';

type SEARCH_MGR_PARAMETERS = {
	key?: string,
	searchType?: ESearchType,
	provider?: ESearchProvider,
	pageNumber?: number,
	compare?: string[]
}

/**
 * Returns an instance of SearchMgr
 * @param {SEARCH_MGR_PARAMETERS} opts - options
 */
export default function useSearchMgr(opts: SEARCH_MGR_PARAMETERS = {}, ): AbstractSearchProvider {
	const {managerRepository} = useGeneralProvider();
	let {key = DEFAULT_KEY, pageNumber = null, compare = [], searchType, provider} = opts;
	const instance = managerRepository.getSearchMgr().getInstance(key, searchType, provider);

	/* Begin block needed to make the hook trigger re-render when the data below changes */
	const searchProvider = (useConfiguration as (prop: string) => unknown)('sitePreferences.searchProvider');
	const quickSearchProvider = (useConfiguration as (prop: string) => unknown)('sitePreferences.quickSearchProvider');

	const searchResult = useSelector(
		(state) => pageNumber
			? selectProductsHits(state, instance.cacheKey, pageNumber)
			: selectProductSearchResultByKey(state, instance.cacheKey),
		(prev, next) => {
			let isEqual = prev?.queryKey === next?.queryKey;

			if (pageNumber) {
				return isEqual;
			} else if (isEqual) {
				for (const key of compare) {
					switch (key) {
						case 'pages':
							isEqual = isEqual &&
								Object.keys(prev?.pages || {}).length === Object.keys(next?.pages || {}).length;

							break;
						default:
							isEqual = isEqual && prev?.[key] === next?.[key];

							break;
					}
				}

				return isEqual;
			}
		}
	);
	/* End block needed to make the hook trigger re-render when the data below changes */

	return useVersioned(instance, searchProvider, quickSearchProvider, searchResult);
};
